import { Injectable, effect, signal, inject, untracked } from '@angular/core';
import { SavedChannel, StreamProject } from '../models/library.model.js';
import { IdbService } from './idb.service.js';
import { SyncService } from './sync.service.js';

const STORAGE_KEYS = {
    CHANNELS: 'tw_analyzer_saved_channels',
    PROJECTS: 'tw_analyzer_stream_projects'
};

@Injectable({
    providedIn: 'root'
})
export class LibraryService {
    // Signals for UI reactivity
    savedChannels = signal<SavedChannel[]>([]);
    streamProjects = signal<StreamProject[]>([]);

    private idbService = inject(IdbService);
    private syncService = inject(SyncService);

    constructor() {
        this.loadFromStorage();

        // When SyncService pulls fresh data from cloud (Pro-only, on login),
        // MERGE remote + local-only entries. Pure replace would lose channels/
        // projects added while offline or non-Pro (Pro→Free→Pro round-trip).
        // After merge we push back so local-only entries land in cloud.
        // Wrapped in untracked so reads of savedChannels/streamProjects inside
        // the merge don't add self-dependencies on the writing effect.
        effect(() => {
            const remote = this.syncService.remoteChannels();
            if (remote === null) return;
            untracked(() => {
                const local = this.savedChannels();
                const merged = this.mergeRemoteAndLocalChannels(remote, local);
                this.savedChannels.set(merged);
                this.saveChannelsToStorage(merged);
                // If we picked up entries the cloud didn't have, push them up.
                const localOnlyCount = merged.length - remote.length;
                if (localOnlyCount > 0) this.syncService.pushChannels(merged);
            });
        });
        effect(() => {
            const remote = this.syncService.remoteProjects();
            if (remote === null) return;
            untracked(() => {
                const local = this.streamProjects();
                const merged = this.mergeRemoteAndLocalProjects(remote, local);
                this.streamProjects.set(merged);
                this.saveProjectsToStorage(merged);
                const localOnly = merged.filter(p => !remote.some(r => r.videoId === p.videoId));
                for (const p of localOnly) this.syncService.pushProject(p);
            });
        });
    }

    // Merge remote channels with any local-only entries (added while non-Pro
    // or offline). Remote shape wins on overlap.
    private mergeRemoteAndLocalChannels(remote: SavedChannel[], local: SavedChannel[]): SavedChannel[] {
        const merged: SavedChannel[] = [...remote];
        const remoteIds = new Set(remote.map(r => r.id));
        for (const l of local) {
            if (!remoteIds.has(l.id)) merged.push(l);
        }
        return merged;
    }

    private mergeRemoteAndLocalProjects(remote: StreamProject[], local: StreamProject[]): StreamProject[] {
        const remoteById = new Map(remote.map(p => [p.videoId, p]));
        const merged: StreamProject[] = [...remote];
        for (const l of local) {
            if (!remoteById.has(l.videoId)) merged.push(l);
        }
        return merged;
    }

    // --- Initialization ---

    private loadFromStorage() {
        try {
            if (typeof localStorage !== 'undefined') {
                const channelsData = localStorage.getItem(STORAGE_KEYS.CHANNELS);
                if (channelsData) {
                    this.savedChannels.set(JSON.parse(channelsData));
                }

                const projectsData = localStorage.getItem(STORAGE_KEYS.PROJECTS);
                if (projectsData) {
                    this.streamProjects.set(JSON.parse(projectsData));
                }
            }
        } catch (e) {
            console.error('Failed to load library data from local storage:', e);
        }
    }

    private saveChannelsToStorage(channels: SavedChannel[]) {
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(STORAGE_KEYS.CHANNELS, JSON.stringify(channels));
            }
        } catch (e) {
            console.error('Failed to save channels:', e);
        }
    }

    private saveProjectsToStorage(projects: StreamProject[]) {
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
            }
        } catch (e) {
            console.error('Failed to save projects:', e);
        }
    }

    // --- Channels Management ---

    addOrUpdateChannel(channel: SavedChannel) {
        this.savedChannels.update(channels => {
            const existingIdx = channels.findIndex(c => c.id === channel.id);
            let updated = [...channels];

            if (existingIdx >= 0) {
                updated[existingIdx] = channel;
            } else {
                updated.push(channel);
            }

            this.saveChannelsToStorage(updated);
            this.syncService.pushChannels(updated);
            return updated;
        });
    }

    removeChannel(channelId: string) {
        this.savedChannels.update(channels => {
            const updated = channels.filter(c => c.id !== channelId);
            this.saveChannelsToStorage(updated);
            this.syncService.pushChannels(updated);
            return updated;
        });
    }

    // --- Projects Management ---

    getProject(videoId: string): StreamProject | undefined {
        return this.streamProjects().find(p => p.videoId === videoId);
    }

    saveProject(project: StreamProject) {
        this.streamProjects.update(projects => {
            const existingIdx = projects.findIndex(p => p.videoId === project.videoId);
            let updated = [...projects];

            project.lastModifiedAt = Date.now();

            if (existingIdx >= 0) {
                updated[existingIdx] = project;
            } else {
                updated.push(project);
            }

            this.saveProjectsToStorage(updated);
            this.syncService.pushProject(project);
            return updated;
        });
    }

    /**
     * Set (or clear, when color is null) the user-chosen color tag for a VOD.
     * Creates a minimal project record if none exists yet — color should survive even if
     * the user has never opened the VOD, it's the first "did I look at this" signal.
     */
    setProjectColor(video: { videoId: string; channelId: string; title: string; createdAt: string; duration: string; thumbnailUrl: string }, color: string | null) {
        let touched: StreamProject | null = null;
        this.streamProjects.update(projects => {
            const existingIdx = projects.findIndex(p => p.videoId === video.videoId);
            let updated = [...projects];
            if (existingIdx >= 0) {
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    userColor: color,
                    lastModifiedAt: Date.now(),
                };
                touched = updated[existingIdx];
            } else {
                touched = {
                    videoId: video.videoId,
                    channelId: video.channelId,
                    title: video.title,
                    createdAt: video.createdAt,
                    duration: video.duration,
                    thumbnailUrl: video.thumbnailUrl,
                    isChatDownloaded: false,
                    userColor: color,
                    lastModifiedAt: Date.now(),
                };
                updated.push(touched);
            }
            this.saveProjectsToStorage(updated);
            return updated;
        });
        if (touched) this.syncService.pushProject(touched);
    }

    markChatAsDownloaded(videoId: string, filePath?: string) {
        let touched: StreamProject | null = null;
        this.streamProjects.update(projects => {
            const existingIdx = projects.findIndex(p => p.videoId === videoId);
            if (existingIdx >= 0) {
                const updated = [...projects];
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    isChatDownloaded: true,
                    chatFilePath: filePath,
                    lastModifiedAt: Date.now()
                };
                touched = updated[existingIdx];
                this.saveProjectsToStorage(updated);
                return updated;
            }
            return projects;
        });
        if (touched) this.syncService.pushProject(touched);
    }

    async deleteProject(videoId: string): Promise<void> {
        // Remove from metadata list
        this.streamProjects.update(projects => {
            const updated = projects.filter(p => p.videoId !== videoId);
            this.saveProjectsToStorage(updated);
            return updated;
        });

        // Remove detailed state from IDB
        await this.idbService.remove(`project_state_${videoId}`);

        // Soft-delete in cloud (no-op for non-Pro users)
        this.syncService.deleteProjectRemote(videoId).catch(e => {
            console.warn('Cloud soft-delete failed:', e);
        });
    }

    async saveProjectState(videoId: string, state: any): Promise<void> {
        await this.idbService.set(`project_state_${videoId}`, state);
    }

    async getProjectState(videoId: string): Promise<any> {
        return await this.idbService.get(`project_state_${videoId}`);
    }
}
